"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import type { User } from "@supabase/supabase-js";

import {
  captureCompletedSignup,
  clearSignupAttempt,
} from "@/lib/analytics/funnel";
import {
  isInternalEmail,
  registerAcquisitionContext,
} from "@/lib/analytics/traffic-context";

/** Root-level identity includes onboarding and direct-to-auth arrivals.
 * Signup completion requires a matching signup attempt, independent of landing
 * experiments. Listen for SPA auth changes as well as hard-navigation returns.
 */
export function MarketingSignupConversionTracker() {
  useEffect(() => {
    if (!posthog.__loaded) return;
    let cancelled = false;
    let revision = 0;
    let unsubscribe: (() => void) | undefined;

    const apply = (user: User | null) => {
      if (cancelled || !user) return;
      try {
        const is_internal = isInternalEmail(user.email);
        posthog.identify(user.id, { is_internal });
        posthog.register({ is_internal });
        captureCompletedSignup(user);
      } catch {
        // Identity/attribution must never interrupt authentication.
      }
    };

    // Keep the auth SDK out of the initial public-page bundle.
    void import("@/lib/supabase/client")
      .then(({ createClient }) => {
        if (cancelled) return;
        const auth = createClient().auth;
        const initialRevision = revision;
        void auth
          .getUser()
          .then(({ data }) => {
            if (revision === initialRevision) apply(data.user);
          })
          .catch(() => {
            /* Unauthenticated or offline. */
          });

        const {
          data: { subscription },
        } = auth.onAuthStateChange((event, session) => {
          revision += 1;
          if (event === "SIGNED_OUT") {
            clearSignupAttempt();
            try {
              posthog.reset();
              registerAcquisitionContext(posthog);
            } catch {
              // Signing out must succeed even if analytics is unavailable.
            }
            return;
          }
          apply(session?.user ?? null);
        });

        unsubscribe = () => subscription.unsubscribe();
      })
      .catch(() => {
        // Failed lazy loading must not affect the page or authentication.
      });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return null;
}
